// frontend/src/utils/policy/resolveEffectiveResponsePolicy.js
//
// POLICY PROVIDER / API INSERTION POINT
// -------------------------------------
// Today this resolver receives one local runtime provider. The function is
// intentionally provider-oriented so later callers may supply authenticated
// tenant, domain, licensed-policy-pack, or remote-API providers without
// rewriting the response validator.
//
// Before external providers are enabled, add:
// 1. caller authentication and tenant binding
// 2. strict schema validation
// 3. scope admissibility checks
// 4. authority/priority conflict resolution
// 5. immutable provenance and audit logging
// 6. protection for non-overridable core rules

import getLocalRuntimeResponsePolicy from './localRuntimePolicyProvider';
import { validateResponsePolicyContract } from './responsePolicyContract';

export function resolveEffectiveResponsePolicy(context = {}, providers = []) {
  const providerList = [
    () => getLocalRuntimeResponsePolicy(context),
    ...providers,
  ];

  const accepted = [];
  const rejected = [];

  providerList.forEach((provider, index) => {
    try {
      const policy = typeof provider === 'function' ? provider(context) : provider;
      const validation = validateResponsePolicyContract(policy);

      if (validation.valid) {
        accepted.push(policy);
      } else {
        rejected.push({ index, policy, errors: validation.errors });
      }
    } catch (error) {
      rejected.push({
        index,
        policy: null,
        errors: [error?.message || 'Policy provider failed.'],
      });
    }
  });

  // Highest authority/priority policy wins for now because only the runtime
  // provider is active. When remote providers are introduced, replace this
  // simple selection with rule-level merging and explicit non-overridable core
  // constraints rather than allowing a whole-policy replacement.
  const effectivePolicy = accepted
    .slice()
    .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0))[0] || null;

  return {
    effectivePolicy,
    acceptedPolicies: accepted,
    rejectedPolicies: rejected,
  };
}

export default resolveEffectiveResponsePolicy;
